import { Skeleton } from "@/components/atoms";
import { Typography } from "@/components/atoms/Typography";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { LogoHeader } from "@/components/molecules/header/LogoHeader";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore } from "@/core/stores/appStore";
import { useManualRegistrationStore } from "@/modules/onboarding/registration/store/registrationStore";
import {
  DOCUMENT_TYPES,
  type DocumentType,
} from "@/modules/profile/constants/documentTypes";
import { downloadDocument } from "@/modules/profile/services/documentDownloadService";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useState } from "react";
import { Dimensions, Image, Platform, ScrollView, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type ViewDocumentType = "emiratesIdFront" | "emiratesIdBack" | "passport";

interface DocumentImage {
  uri: string;
  label: string;
}

// Calculate responsive image dimensions based on screen width
// Using same aspect ratio as upload document screen (300:169.139 ≈ 1.77:1)
const getImageDimensions = () => {
  const screenWidth = Dimensions.get("window").width;
  const padding = 48; // 24px padding on each side
  const imageWidth = screenWidth - padding;
  const imageHeight = imageWidth / 1.77; // Maintain 16:9 aspect ratio
  return { width: imageWidth, height: imageHeight };
};

function mimeTypeForDocumentExtension(extension: string): string {
  const normalized = extension.toLowerCase();
  if (normalized === "jpg" || normalized === "jpeg") {
    return "image/jpeg";
  }
  return "image/png";
}

async function downloadViewerDocument(
  doc: { docType: DocumentType; label: string },
  customerId: string,
): Promise<DocumentImage | null> {
  try {
    logger.info("DocumentViewerScreen", "Downloading document", {
      docType: doc.docType,
      customerId,
    });

    const response = await downloadDocument(doc.docType, customerId);
    const fileContent = response.details?.fileContent;
    if (response.status !== "success" || !fileContent) {
      logger.debug("DocumentViewerScreen", "Document not available", {
        docType: doc.docType,
        status: response.status,
        message: response.message || "Unknown error",
        hasDetails: !!response.details,
        hasFileContent: !!fileContent,
      });
      return null;
    }

    if (fileContent.trim().length === 0) {
      logger.warn("DocumentViewerScreen", "Empty file content", {
        docType: doc.docType,
      });
      return null;
    }

    try {
      const extension = (response.details?.extension || "png").toLowerCase();
      const dataUri = `data:${mimeTypeForDocumentExtension(extension)};base64,${fileContent}`;
      logger.info("DocumentViewerScreen", "Document loaded successfully", {
        docType: doc.docType,
        label: doc.label,
        extension,
        contentLength: fileContent.length,
      });
      return { uri: dataUri, label: doc.label };
    } catch (processingError) {
      logger.error("DocumentViewerScreen", "Failed to process document", {
        docType: doc.docType,
        error: processingError,
        hasFileContent: true,
      });
      return null;
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Unexpected error";
    logger.error("DocumentViewerScreen", "Unexpected error downloading document", {
      docType: doc.docType,
      error: message,
      stack: err instanceof Error ? err.stack : undefined,
    });
    return null;
  }
}

export function DocumentViewerScreen() {
  useCatalogDynatraceView(DT_SCREEN.DOCUMENT_VIEWER, 'document_viewer_viewed', true);
  const { t } = useTranslation();
  const colors = useUIColors();
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams<{ documentType?: string }>();
  const user = useAppStore((state) => state.auth.user);
  const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);
  const registrationStore = useManualRegistrationStore();
  const { isRTL } = useI18nContext();

  // Get responsive image dimensions
  const imageDimensions = getImageDimensions();

  // Get customer_id from authenticated user OR registration store (for registration flow)
  // But only use registration store customerId if we're actually authenticated (has token)
  // During registration flow, documents are uploaded, not downloaded via this screen
  const customerId =
    user?.customer_id ||
    (isAuthenticated && registrationStore.registrationResponse?.customer_id) ||
    null;

  const documentType =
    (params.documentType as ViewDocumentType) || "emiratesIdFront";
  const [documents, setDocuments] = useState<DocumentImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);

  // Determine which documents to show based on document type
  const getDocumentsToLoad = (): Array<{
    docType: DocumentType;
    label: string;
  }> => {
    if (documentType === "passport") {
      return [
        {
          docType: DOCUMENT_TYPES.PASSPORT,
          label: t("documentViewer.passport"),
        },
      ];
    } else if (documentType === "emiratesIdFront") {
      return [
        { docType: DOCUMENT_TYPES.EID_FRONT, label: t("documentViewer.front") },
        { docType: DOCUMENT_TYPES.EID_BACK, label: t("documentViewer.back") },
      ];
    } else {
      // Default to Emirates ID front and back
      return [
        { docType: DOCUMENT_TYPES.EID_FRONT, label: t("documentViewer.front") },
        { docType: DOCUMENT_TYPES.EID_BACK, label: t("documentViewer.back") },
      ];
    }
  };

  // Get screen title based on document type
  const getScreenTitle = (): string => {
    if (documentType === "passport") {
      return t("documentViewer.passport");
    }
    return t("profile.emiratesId");
  };

  useEffect(() => {
    const loadDocuments = async () => {
      // Don't make API calls if user is not authenticated
      // Document download requires authentication token
      if (!isAuthenticated) {
        logger.warn(
          "DocumentViewerScreen",
          "User not authenticated, skipping document download",
          {
            hasUser: !!user,
            isAuthenticated,
          },
        );
        setError("Authentication required to view documents");
        setErrorDialogVisible(true);
        setLoading(false);
        return;
      }

      if (!customerId) {
        logger.error("DocumentViewerScreen", "Missing customer_id", {
          hasUser: !!user,
          hasRegistrationResponse: !!registrationStore.registrationResponse,
          userCustomerId: user?.customer_id,
          registrationCustomerId:
            registrationStore.registrationResponse?.customer_id,
          isAuthenticated,
        });
        setError("Missing customer information");
        setErrorDialogVisible(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const documentsToLoad = getDocumentsToLoad();
        const downloadedDocuments = await Promise.all(
          documentsToLoad.map((doc) =>
            downloadViewerDocument(doc, customerId),
          ),
        );
        const loadedDocuments = downloadedDocuments.filter(
          (document): document is DocumentImage => document !== null,
        );

        if (loadedDocuments.length === 0) {
          // No documents available - show user-friendly message
          logger.info(
            "DocumentViewerScreen",
            "No documents available for user",
            {
              documentType,
              documentsToLoad: documentsToLoad.length,
            },
          );
          setError(t("profile.noDocumentsAvailable"));
          setErrorDialogVisible(true);
        } else {
          // Show available documents (even if some failed)
          logger.info("DocumentViewerScreen", "Loaded documents", {
            loadedCount: loadedDocuments.length,
            requestedCount: documentsToLoad.length,
          });
          setDocuments(loadedDocuments);
        }
      } catch (err) {
        logger.error("DocumentViewerScreen", "Failed to load documents", err);
        setError("Failed to load documents");
        setErrorDialogVisible(true);
      } finally {
        setLoading(false);
      }
    };

    void loadDocuments();
  }, [
    documentType,
    customerId,
    isAuthenticated,
    t,
    user,
    registrationStore.registrationResponse,
  ]);

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
    >
      <LogoHeader hideLogo={true} showLanguageSwitch={false} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: insets.bottom + 24 },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Title */}
        <Typography
          variant="title1"
          color="primary"
          style={[styles.title, Platform.OS === "web" && isRTL && styles.titleRTL]}        >
          {getScreenTitle()}
        </Typography>

        {loading ? (
          // Skeleton loading state
          <>
            {getDocumentsToLoad().map((_, index) => (
              <View key={index} style={styles.documentContainer}>
                <View
                  style={[
                    styles.imageContainer,
                    { backgroundColor: colors.backgroundLevel2 },
                  ]}
                >
                  <Skeleton
                    width={imageDimensions.width}
                    height={imageDimensions.height}
                    borderRadius={12}
                  />
                </View>
                <Skeleton
                  width={120}
                  height={20}
                  borderRadius={4}
                  style={styles.skeletonLabel}
                />
              </View>
            ))}
          </>
        ) : (
          // Document Images
          <>
            {documents.map((doc, index) => (
              <View key={index} style={styles.documentContainer}>
                <View
                  style={[
                    styles.imageContainer,
                    { backgroundColor: colors.backgroundLevel2 },
                  ]}
                >
                  <Image
                    source={{ uri: doc.uri }}
                    style={[
                      styles.documentImage,
                      {
                        width: imageDimensions.width,
                        height: imageDimensions.height,
                      },
                    ]}
                    resizeMode="cover"
                  />
                </View>
                <Typography
                  variant="headline"
                  color="primary"
                  style={styles.documentLabel}
                >
                  {doc.label}
                </Typography>
              </View>
            ))}

            {documents.length === 0 && !loading && (
              <View style={styles.emptyContainer}>
                <Typography
                  variant="body"
                  color="tertiary"
                  style={styles.emptyText}
                >
                  {t("profile.noIdProofsAvailable")}
                </Typography>
              </View>
            )}
          </>
        )}
      </ScrollView>

      {/* Error Dialog */}
      <OkDialog
        visible={errorDialogVisible}
        message={error || t("common.error")}
        onOk={() => {
          setErrorDialogVisible(false);
          router.back();
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 32,
    paddingHorizontal: 16,
    gap: 32,
  },
  title: {
    marginBottom: 0,
  },
  titleRTL: {
    textAlign: "right",
  },
  documentContainer: {
    gap: 8,
    alignItems: "center",
  },
  imageContainer: {
    borderRadius: 16,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  documentImage: {
    borderRadius: 12,
  },
  documentLabel: {
    textAlign: "center",
    marginTop: 8,
  },
  skeletonLabel: {
    marginTop: 8,
    alignSelf: "center",
  },
  emptyContainer: {
    paddingVertical: 32,
    alignItems: "center",
  },
  emptyText: {
    textAlign: "center",
  },
});
